import { useEffect, useState } from "react";
import { Plus } from "lucide-react";

import { api, errMsg } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { useToast } from "../../../context/ToastContext.js";
import { useConfirm } from "../../../components/ui/confirm-dialog.js";
import { DateInput } from "../../../components/ui/date-input.js";
import { Combobox } from "../../../components/ui/combobox.js";
import { Button } from "../../../components/ui/button.js";
import { Input } from "../../../components/ui/input.js";
import { Textarea } from "../../../components/ui/textarea.js";
import { Badge } from "../../../components/ui/badge.js";
import { DropdownSelect } from "../../../components/DropdownButton.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../components/ui/table.js";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../../components/ui/dialog.js";
import { DeleteIconButton, EditIconButton, EmptyState, Field, SectionCard, fmtDate } from "../shared.js";
import { skillDisplayName, type EmployeeSkillRow, type ExpertiseLevel, type ReviewDetail, type SkillRow } from "../types.js";

// Sentinel for the "Not rated" dropdown option: a Radix Select item cannot use
// an empty-string value, so this stands in for "no expertise level" and maps to
// null on save.
const NOT_RATED = "__NOT_RATED__";

// Spec 4.A. The employee never edits their own skills here; a manager or HR
// editing someone else's review does. The server re-checks that on every write.
export default function SkillsSection({
  review,
  onChanged,
}: {
  review: ReviewDetail;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const canEdit = review.capabilities.canEditSkills;

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<EmployeeSkillRow | null>(null);

  async function remove(row: EmployeeSkillRow) {
    const ok = await confirm({
      title: "Remove this skill?",
      description: `${row.displayName} will be removed from this employee, with its action plan and comments.`,
      actionText: "Remove",
      tone: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/employee-skills/${row.id}`);
      toast.show("Skill removed", "success");
      onChanged();
    } catch (e) {
      logError("SkillsSection.remove", e);
      toast.show(errMsg(e) ?? "Could not remove the skill", "error");
    }
  }

  return (
    <SectionCard
      title="Skills"
      collapsible
      description="Skills assigned to this employee, with their expertise level and any agreed action plan."
      actions={
        canEdit ? (
          <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Add skill
          </Button>
        ) : undefined
      }
    >
      {review.skills.length === 0 ? (
        <EmptyState message="No skills are assigned to this employee yet." />
      ) : (
        <div className="overflow-x-auto rounded-md border border-surface-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-3">Type</TableHead>
                <TableHead>Skill</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Agreed action plan</TableHead>
                <TableHead>Review on</TableHead>
                <TableHead>Knowledge sharing</TableHead>
                <TableHead>Comment</TableHead>
                <TableHead className="pr-3 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {review.skills.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="pl-3 text-surface-500">{s.skillTypeName}</TableCell>
                  <TableCell className="font-medium">{s.skillName}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {s.expertiseLevelName && (
                        <Badge tone={s.belowExpert ? "warning" : "success"}>
                          {s.expertiseLevelName} · {s.weightPercent}%
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    {s.agreedActionPlan ? (
                      <span className="whitespace-pre-wrap">{s.agreedActionPlan}</span>
                    ) : s.belowExpert ? (
                      <span className="text-surface-400">Not agreed yet</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-surface-500">
                    {s.actionPlanReviewOn ? fmtDate(s.actionPlanReviewOn) : null}
                  </TableCell>
                  <TableCell>
                    {s.knowledgeSharing == null ? null : (
                      <Badge tone={s.knowledgeSharing ? "success" : "neutral"}>
                        {s.knowledgeSharing ? "Yes" : "No"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="max-w-xs">
                    {s.comment ? <span className="whitespace-pre-wrap">{s.comment}</span> : null}
                  </TableCell>
                  <TableCell className="pr-3 text-center">
                    {canEdit && (
                      <div className="flex items-center justify-center gap-0.5">
                        <EditIconButton name={s.displayName} onClick={() => setEditing(s)} />
                        <DeleteIconButton name={s.displayName} onClick={() => void remove(s)} />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddSkillDialog
        open={showAdd}
        employeeId={review.header.employeeId}
        existingSkillIds={review.skills.map((s) => s.skillId)}
        onClose={() => setShowAdd(false)}
        onSaved={onChanged}
      />
      <EditSkillDialog row={editing} onClose={() => setEditing(null)} onSaved={onChanged} />
    </SectionCard>
  );
}

function AddSkillDialog({
  open,
  employeeId,
  existingSkillIds,
  onClose,
  onSaved,
}: {
  open: boolean;
  employeeId: string;
  existingSkillIds: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [skillTypeId, setSkillTypeId] = useState("");
  const [skillId, setSkillId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSkillTypeId("");
    setSkillId("");
    setError("");
    api
      .get<SkillRow[]>(`/skills?forEmployeeId=${employeeId}`)
      .then((s) => setSkills(s.data))
      .catch((e) => logError("SkillsSection.catalog", e));
  }, [open, employeeId]);

  async function save() {
    setSaving(true);
    setError("");
    try {
      // The level is deliberately not asked here — a new skill starts "Not
      // rated" and the manager sets the level later via Edit.
      await api.post(`/employees/${employeeId}/skills`, { skillId, expertiseLevelId: null });
      toast.show("Skill added", "success");
      onSaved();
      onClose();
    } catch (e) {
      logError("SkillsSection.add", e);
      setError(errMsg(e) ?? "Could not add the skill");
    } finally {
      setSaving(false);
    }
  }

  // Skills that fit this employee and they do not have yet. The type list is
  // built from those, so every type offered has at least one skill to pick.
  const available = skills.filter((s) => !existingSkillIds.includes(s.id));
  const typeOptions = Array.from(new Map(available.map((s) => [s.skillType.id, s.skillType.name])).entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const skillsOfType = available.filter((s) => s.skillTypeId === skillTypeId);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a skill</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          {/* Only the type and skill are chosen here; the expertise level is set
              later via Edit, so a new skill starts "Not rated". */}
          <Field label="Skill type">
            <Combobox
              value={skillTypeId}
              onValueChange={(v) => {
                setSkillTypeId(v);
                setSkillId("");
              }}
              options={typeOptions}
              placeholder="Select a skill type"
              searchPlaceholder="Search skill types"
              emptyText="No skill type matches."
            />
          </Field>
          <Field label="Skill">
            <Combobox
              value={skillId}
              onValueChange={setSkillId}
              disabled={!skillTypeId}
              options={skillsOfType.map((s) => ({ value: s.id, label: skillDisplayName(s) }))}
              placeholder={skillTypeId ? "Select a skill" : "Select a skill type first"}
              searchPlaceholder="Search skills"
              emptyText="No skill matches."
            />
          </Field>
          {available.length === 0 && skills.length > 0 && (
            <p className="text-[13px] text-surface-500">Every skill that fits this employee is already assigned.</p>
          )}
          {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || !skillId} onClick={() => void save()}>
            {saving ? "Adding…" : "Add skill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditSkillDialog({
  row,
  onClose,
  onSaved,
}: {
  row: EmployeeSkillRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [levels, setLevels] = useState<ExpertiseLevel[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [form, setForm] = useState({
    expertiseLevelId: "",
    agreedActionPlan: "",
    actionPlanReviewOn: "",
    actionPlanCompleted: false,
    knowledgeSharing: "UNSET",
    comment: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!row) return;
    setForm({
      expertiseLevelId: row.expertiseLevelId ?? NOT_RATED,
      agreedActionPlan: row.agreedActionPlan ?? "",
      actionPlanReviewOn: row.actionPlanReviewOn?.slice(0, 10) ?? "",
      actionPlanCompleted: row.actionPlanCompleted,
      knowledgeSharing: row.knowledgeSharing == null ? "UNSET" : row.knowledgeSharing ? "YES" : "NO",
      comment: row.comment ?? "",
    });
    api
      .get<ExpertiseLevel[]>("/expertise-levels")
      .then((r) => setLevels(r.data))
      .catch((e) => logError("SkillsSection.levels", e));
  }, [row]);

  const triState = (v: string): boolean | null => (v === "UNSET" ? null : v === "YES");

  async function save() {
    if (!row) return;
    setSaving(true);
    setError("");
    try {
      await api.patch(`/employee-skills/${row.id}`, {
        expertiseLevelId: form.expertiseLevelId === NOT_RATED ? null : form.expertiseLevelId,
        agreedActionPlan: form.agreedActionPlan || null,
        actionPlanReviewOn: form.actionPlanReviewOn || null,
        actionPlanCompleted: form.actionPlanCompleted,
        knowledgeSharing: triState(form.knowledgeSharing),
        comment: form.comment || null,
      });
      toast.show("Skill updated", "success");
      onSaved();
      onClose();
    } catch (e) {
      logError("SkillsSection.update", e);
      setError(errMsg(e) ?? "Could not update the skill");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={row != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{row?.displayName}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Expertise level">
            <DropdownSelect
              value={form.expertiseLevelId}
              onChange={(v) => setForm((f) => ({ ...f, expertiseLevelId: v }))}
              options={[
                { value: NOT_RATED, label: "Not rated" },
                ...levels.map((l) => ({ value: l.id, label: `${l.name} · ${l.weightPercent}%` })),
              ]}
              placeholder="Not rated"
              ariaLabel="Expertise level"
              clearable={false}
              className="w-full justify-between"
              open={openDropdown === "expertise"}
              onToggle={() => setOpenDropdown((d) => (d === "expertise" ? null : "expertise"))}
              onClose={() => setOpenDropdown((d) => (d === "expertise" ? null : d))}
            />
          </Field>
          <Field label="Agreed action plan">
            <Textarea
              value={form.agreedActionPlan}
              onChange={(e) => setForm((f) => ({ ...f, agreedActionPlan: e.target.value }))}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Review on">
              <DateInput isUS
                value={form.actionPlanReviewOn}
                onChange={(v) => setForm((f) => ({ ...f, actionPlanReviewOn: v }))}
              />
            </Field>
            <Field label="Action plan completed">
              <DropdownSelect
                value={form.actionPlanCompleted ? "YES" : "NO"}
                onChange={(v) => setForm((f) => ({ ...f, actionPlanCompleted: v === "YES" }))}
                options={ACTION_PLAN_OPTIONS}
                placeholder="Not yet"
                ariaLabel="Action plan completed"
                clearable={false}
                className="w-full justify-between"
                open={openDropdown === "actionPlan"}
                onToggle={() => setOpenDropdown((d) => (d === "actionPlan" ? null : "actionPlan"))}
                onClose={() => setOpenDropdown((d) => (d === "actionPlan" ? null : d))}
              />
            </Field>
            <Field label="Knowledge sharing">
              <TriSelect
                value={form.knowledgeSharing}
                onChange={(v) => setForm((f) => ({ ...f, knowledgeSharing: v }))}
              />
            </Field>
          </div>
          <Field label="Comment">
            <Textarea
              value={form.comment}
              onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
            />
          </Field>
          {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTION_PLAN_OPTIONS = [
  { value: "NO", label: "Not yet" },
  { value: "YES", label: "Completed" },
];

const TRI_OPTIONS = [
  { value: "UNSET", label: "Not set" },
  { value: "YES", label: "Yes" },
  { value: "NO", label: "No" },
];

function TriSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  // One of these per skill row, so each owns its own open state rather than
  // threading a shared key down from the section.
  const [open, setOpen] = useState(false);
  return (
    <DropdownSelect
      value={value}
      onChange={onChange}
      options={TRI_OPTIONS}
      placeholder="Not set"
      ariaLabel="Skill assessment"
      clearable={false}
      className="w-full justify-between"
      open={open}
      onToggle={() => setOpen((o) => !o)}
      onClose={() => setOpen(false)}
    />
  );
}
